import { LngLatBoundsLike, Map as MapLibreMap } from 'maplibre-gl';

import { COUNTRY_CENTERS } from '../../MapSharedData';

type Bounds = [[number, number], [number, number]];

interface FlyToCountryOptions {
  map: MapLibreMap;
  bounds: Bounds;
  countryCode: string;
  duration?: number;
  padding?: number;
  maxZoom?: number;
}

/**
 * Smoothly fly the camera to a country's bounds.
 *
 * Countries that cross the antimeridian (USA with Alaska, Russia, Fiji,
 * NZ with Chatham Islands) produce bounds spanning ~330° of longitude.
 * `fitBounds` on those would zoom out to show the whole globe — landing
 * the camera somewhere in the Atlantic. Detect that case and fall back
 * to a fixed center+zoom from COUNTRY_CENTERS, which keeps the camera
 * on the country's main landmass.
 */
export function flyToCountryBounds({
  map,
  bounds,
  countryCode,
  duration = 1100,
  padding = 40,
  maxZoom = 7,
}: FlyToCountryOptions) {
  const [[w, s], [e, n]] = bounds;
  const lngSpan = e - w;
  const latSpan = n - s;
  const spansAntimeridian = lngSpan > 180 || latSpan > 80;

  const fallbackCenter = COUNTRY_CENTERS[countryCode];
  if (spansAntimeridian && fallbackCenter) {
    map.flyTo({
      center: fallbackCenter,
      zoom: 3, // Roughly fits a continental country
      duration,
      curve: 1.1,
      essential: true,
    });
    return;
  }

  // Normal case — cameraForBounds gives us the fitted center + zoom,
  // and flyTo interpolates both smoothly from the current camera position.
  const cam = map.cameraForBounds(bounds as LngLatBoundsLike, { padding, maxZoom });
  if (cam && cam.center && typeof cam.zoom === 'number') {
    map.easeTo({
      center: cam.center as [number, number],
      zoom: cam.zoom,
      duration,
      essential: true,
    });
  } else {
    map.fitBounds(bounds as LngLatBoundsLike, { padding, duration, maxZoom, essential: true });
  }
}

interface FlyToFeatureOptions {
  map: MapLibreMap;
  feature: GeoJSON.Feature;
  duration?: number;
  padding?: number;
  maxZoom?: number;
}

/**
 * Smoothly fly to a single region/feature. Same antimeridian-aware logic as
 * flyToCountryBounds, but the fallback center comes from the feature's
 * largest polygon centroid (we don't have a hand-maintained center lookup
 * for every region of every country).
 */
export function flyToFeatureBounds({
  map,
  feature,
  duration = 800,
  padding = 60,
  maxZoom = 10,
}: FlyToFeatureOptions) {
  // Compute the feature's bbox by walking all coordinates
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  const visit = (coords: any) => {
    if (typeof coords[0] === 'number') {
      const [x, y] = coords;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      return;
    }
    for (const c of coords) visit(c);
  };
  const g = feature.geometry as GeoJSON.Geometry & { coordinates?: any };
  if (!g?.coordinates) return;
  visit(g.coordinates);
  if (!isFinite(minX)) return;

  const lngSpan = maxX - minX;
  const latSpan = maxY - minY;
  const spansAntimeridian = lngSpan > 180 || latSpan > 80;

  if (spansAntimeridian) {
    // For wrapping features (Alaska, Russia's Chukotka), compute the bbox of
    // the LARGEST polygon ring only — that skips the small far-flung pieces
    // (Aleutian Islands crossing 180°, Russia's Wrangel Island) that pull
    // the bbox to the whole globe.
    let bestRing: number[][] | null = null;
    const collect = (coords: any) => {
      if (Array.isArray(coords) && Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
        if (!bestRing || coords.length > bestRing.length) bestRing = coords;
        return;
      }
      if (Array.isArray(coords)) for (const c of coords) collect(c);
    };
    collect(g.coordinates);

    if (bestRing) {
      // Bbox of just the largest ring
      let rx0 = Infinity,
        ry0 = Infinity,
        rx1 = -Infinity,
        ry1 = -Infinity;
      for (const [x, y] of bestRing as number[][]) {
        if (x < rx0) rx0 = x;
        if (y < ry0) ry0 = y;
        if (x > rx1) rx1 = x;
        if (y > ry1) ry1 = y;
      }
      const ringBounds: Bounds = [
        [rx0, ry0],
        [rx1, ry1],
      ];
      const cam = map.cameraForBounds(ringBounds as LngLatBoundsLike, { padding, maxZoom });
      if (cam && cam.center && typeof cam.zoom === 'number') {
        map.flyTo({
          center: cam.center as [number, number],
          zoom: cam.zoom,
          duration,
          // Smoother curve for large zoom deltas — default (1.42) over-arcs
          // through a very low zoom for animations like world→region, which
          // feels jerky. Lower curve = more direct path.
          curve: 1.1,
          essential: true,
        });
        return;
      }
    }
  }

  // Normal case
  const bounds: Bounds = [
    [minX, minY],
    [maxX, maxY],
  ];
  const cam = map.cameraForBounds(bounds as LngLatBoundsLike, { padding, maxZoom });
  if (cam && cam.center && typeof cam.zoom === 'number') {
    map.flyTo({
      center: cam.center as [number, number],
      zoom: cam.zoom,
      duration,
      curve: 1.1,
      essential: true,
    });
  } else {
    map.fitBounds(bounds as LngLatBoundsLike, { padding, duration, maxZoom, essential: true });
  }
}
